import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";

const styles = {
  closeButton: {
    position: "absolute",
    right: 8,
    top: 8,
  },
  billingTypeItem: {
    width: "100%",
    p: 2,
    mb: 1.5,
    textAlign: "left",
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 1,
    justifyContent: "flex-start",
    alignItems: "flex-start",
    "&:hover": {
      backgroundColor: "rgba(25, 118, 210, 0.04)",
      borderColor: "rgba(25, 118, 210, 0.3)",
    },
    "&:last-child": {
      mb: 0,
    },
  },
};

export default function InboundBillingTypeSelectionModal({ 
  open, 
  onClose, 
  billingTypes = [], 
  billingComments = {},
  onSelect, 
  selectedBillingType,
  onBack
}) {
  const [currentSelection, setCurrentSelection] = useState(selectedBillingType || "");

  // Only allow closing if a selection has been made
  const canClose = currentSelection !== "";

  useEffect(() => {
    // If the saved billing type is "TBD", select "I'm not sure"
    if (selectedBillingType === "TBD") {
      setCurrentSelection("I'm not sure");
    } else {
      setCurrentSelection(selectedBillingType || "");
    }
  }, [selectedBillingType]);

  const handleSelectBillingType = (billingType) => {
    setCurrentSelection(billingType);
    // Automatically proceed when a billing type is selected
    onSelect(billingType);
  };

  const handleNext = () => {
    if (currentSelection) {
      onSelect(currentSelection);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={canClose ? onClose : undefined}
      disableEscapeKeyDown={!canClose}
      maxWidth={false}
      PaperProps={{
        sx: { 
          width: "520px", 
          maxWidth: "90vw"
        }
      }}
    >
      <DialogTitle>
        <Typography fontSize={24} fontWeight={500} color="textPrimary" align="center">
          Select Billing Type
        </Typography>
        <IconButton 
          onClick={canClose ? onClose : undefined} 
          disabled={!canClose}
          sx={{
            ...styles.closeButton,
            opacity: canClose ? 1 : 0.3,
            cursor: canClose ? 'pointer' : 'not-allowed'
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box mt={2}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: "center" }}>
            Use the notes under each option to select the billing type that matches this inbound transaction.
          </Typography>

          {billingTypes.length === 0 ? (
            <Typography variant="body1" color="text.secondary" align="center" py={4}>
              No billing types available
            </Typography>
          ) : (
            <>
              {billingTypes.map((billingType, index) => (
                <React.Fragment key={billingType}>
                  {index > 0 && <Divider sx={{ my: 1 }} />}
                  <Button
                    variant="outlined"
                    sx={{
                      ...styles.billingTypeItem,
                      ...(currentSelection === billingType && {
                        backgroundColor: "rgba(25, 118, 210, 0.08)",
                        borderColor: "rgba(25, 118, 210, 0.5)",
                        "&:hover": {
                          backgroundColor: "rgba(25, 118, 210, 0.08)",
                          borderColor: "rgba(25, 118, 210, 0.5)",
                        },
                      }),
                    }}
                    onClick={() => handleSelectBillingType(billingType)}
                  >
                    <Box display="flex" flexDirection="column" alignItems="flex-start" gap={0.5}>
                      <Typography variant="body1" fontWeight={500} color="text.primary">
                        {billingType}
                        {index === 0 && (
                          <Typography component="span" variant="body1" color="primary.main" sx={{ fontWeight: 500, ml: 1 }}>
                            (most common)
                          </Typography>
                        )}
                      </Typography>
                      {billingComments[billingType] && (
                        <Typography variant="body1" color="text.secondary">
                          {billingComments[billingType]}
                        </Typography>
                      )}
                    </Box>
                  </Button>
                </React.Fragment>
              ))}

              {/* "I'm not sure" option - only show for multiple billing types */}
              {/* {billingTypes.length > 1 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Button
                    variant="outlined"
                    sx={{
                      ...styles.billingTypeItem,
                      ...(currentSelection === "I'm not sure" && {
                        backgroundColor: "rgba(25, 118, 210, 0.08)",
                        borderColor: "rgba(25, 118, 210, 0.5)",
                        "&:hover": {
                          backgroundColor: "rgba(25, 118, 210, 0.08)",
                          borderColor: "rgba(25, 118, 210, 0.5)",
                        },
                      }),
                    }}
                    onClick={() => handleSelectBillingType("I'm not sure")}
                  >
                    <Box display="flex" flexDirection="column" alignItems="flex-start" gap={0.5}>
                      <Typography variant="body1" fontWeight={500} color="text.primary">
                        I'm Not Sure
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        We'll notify the office staff for assistance.
                      </Typography>
                    </Box>
                  </Button>
                </>
              )} */}
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={onBack}
          variant="outlined"
          color="primary"
        >
          Back
        </Button>
        <Button 
          onClick={handleNext}
          variant="contained"
          color="primary"
          disabled={!currentSelection}
        >
          Next
        </Button>
      </DialogActions>
    </Dialog>
  );
}
